// Layer: presentation. Depends on application + domain only.
import { useCallback, useEffect, useState } from "react";
import type { DecideAction, LoadShift } from "../application/shift-load";
import { countPending, withoutAction } from "../application/shift-load";
import {
  ARCS,
  ARC_LABELS,
  ApiError,
  type Action,
  type Arc,
  type Decision,
  type ShiftView,
} from "../domain/types";

interface Props {
  propertyId: string;
  loadShift: LoadShift;
  decideAction: DecideAction;
}

export function ShiftViewPanel({ propertyId, loadShift, decideAction }: Props) {
  const [view, setView] = useState<ShiftView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    loadShift
      .exec(propertyId)
      .then(setView)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [loadShift, propertyId]);

  useEffect(load, [load]);

  const decide = async (action: Action, decision: Decision): Promise<void> => {
    setBusy(action.id);
    setError(null);
    try {
      await decideAction.exec(action.id, decision);
      setView((current) => (current ? withoutAction(current, action.id) : current));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };

  if (error && !view) {
    return <ErrorPanel message={error} onRetry={load} />;
  }
  if (!view) {
    return <p className="muted" role="status">Loading shift…</p>;
  }

  return (
    <main aria-label="Shift view" className="stack">
      <header className="panel-head">
        <h2>Shift</h2>
        <p className="muted">{countPending(view)} awaiting you</p>
      </header>
      {error ? <div className="alert" role="alert">{error}</div> : null}
      {ARCS.map((arc) => (
        <ArcSection
          key={arc}
          arc={arc}
          items={view[arc]}
          busyId={busy}
          onDecide={decide}
        />
      ))}
    </main>
  );
}

function ArcSection({
  arc,
  items,
  busyId,
  onDecide,
}: {
  arc: Arc;
  items: Action[];
  busyId: string | null;
  onDecide: (action: Action, decision: Decision) => Promise<void>;
}) {
  return (
    <section className="card" aria-labelledby={`arc-${arc}`}>
      <h3 id={`arc-${arc}`}>
        {ARC_LABELS[arc]} <span className="count">{items.length}</span>
      </h3>
      {items.length === 0 ? (
        <p className="muted">Nothing waiting.</p>
      ) : (
        <ul className="action-list">
          {items.map((action) => (
            <ActionCard
              key={action.id}
              action={action}
              busy={busyId === action.id}
              onDecide={onDecide}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function ActionCard({
  action,
  busy,
  onDecide,
}: {
  action: Action;
  busy: boolean;
  onDecide: (action: Action, decision: Decision) => Promise<void>;
}) {
  return (
    <li className="action">
      <div className="action-head">
        <strong>{action.kind}</strong>
        <span className={`badge badge-${action.status}`}>
          {action.status.replace("_", " ")}
        </span>
        {!action.reversible ? (
          <span className="badge badge-warn" title="This action cannot be undone">
            irreversible
          </span>
        ) : null}
      </div>
      {/* FR-PA-6: every action is one tap from its reasoning. */}
      <ul className="why">
        {action.reasoning.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <div className="actions">
        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={() => void onDecide(action, "approve")}
        >
          Approve
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void onDecide(action, "reject")}
        >
          Reject
        </button>
      </div>
    </li>
  );
}

export function ErrorPanel({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="alert" role="alert">
      <p>{message}</p>
      {onRetry ? (
        <button type="button" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}
